const BASE = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const headers = () => ({
  "Content-Type": "application/json",
  ...(localStorage.getItem("token") ? { Authorization: `Bearer ${localStorage.getItem("token")}` } : {})
});

// El cuerpo se lee como TEXTO y luego se intenta parsear, no con res.json()
// directamente. Con res.json(), una respuesta sin cuerpo —el backend caido, el
// proxy de desarrollo sin nadie al otro lado, un 502 de la pasarela— reventaba
// con "Unexpected end of JSON input", que no dice ni el codigo HTTP ni la ruta y
// obliga a abrir la pestana de red para saber que ha pasado.
const handle = async res => {
  const texto = await res.text();
  let data = null;
  if (texto) { try { data = JSON.parse(texto); } catch { /* no es JSON */ } }

  if (!res.ok) {
    // El codigo del backend viaja con el error: hay pantallas que distinguen
    // "no has conectado Odoo" de un fallo de verdad, y hacerlo por el texto del
    // mensaje se rompe en cuanto alguien reescribe la frase.
    const err = new Error(
      (data && data.error) ||
      (texto ? `El servidor respondió ${res.status}` : `El servidor no respondió (${res.status})`)
    );
    if (data && data.code) err.code = data.code;
    err.status = res.status;
    throw err;
  }

  // 2xx con un cuerpo que no es JSON: casi siempre es el HTML de otro servidor
  // (el proxy sirviendo el index en vez de la API). Decirlo asi ahorra el rato
  // de buscar el fallo en el sitio equivocado.
  if (texto && data === null) {
    const err = new Error("El servidor respondió algo que no es JSON");
    err.status = res.status;
    throw err;
  }
  return data;
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const login   = (email, password) =>
  fetch(`${BASE}/auth/login`, { method:"POST", headers: headers(), body: JSON.stringify({ email, password }) }).then(handle);


export const createUser = data =>
  fetch(`${BASE}/users`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const updateUser = (id, data) =>
  fetch(`${BASE}/users/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(data) }).then(handle);


// ── Teams ─────────────────────────────────────────────────────────────────────
export const getTeams   = () =>
  fetch(`${BASE}/teams`, { headers: headers() }).then(handle);

export const createTeam = data =>
  fetch(`${BASE}/teams`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const updateTeam = (id, data) =>
  fetch(`${BASE}/teams/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const deleteTeam = id =>
  fetch(`${BASE}/teams/${id}`, { method:"DELETE", headers: headers() }).then(handle);

// ── Agenda ────────────────────────────────────────────────────────────────────
export const getAgenda   = (user_id) =>
  fetch(`${BASE}/agenda${user_id ? `?user_id=${user_id}` : ""}`, { headers: headers() }).then(handle);

export const createAgendaEntry = data =>
  fetch(`${BASE}/agenda`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const updateAgendaEntry = (id, data) =>
  fetch(`${BASE}/agenda/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(data) }).then(handle);

// El destinatario y el equipo que le tocan a una incidencia por el contacto de su ticket (solo lee; es del equipo TIC)
export const destinoIncidencia = data =>
  fetch(`${BASE}/agenda/destino-incidencia`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const deleteAgendaEntry = id =>
  fetch(`${BASE}/agenda/${id}`, { method:"DELETE", headers: headers() }).then(handle);

export const importAgendaEntries = entries =>
  fetch(`${BASE}/agenda/import`, { method:"POST", headers: headers(), body: JSON.stringify({ entries }) }).then(handle);

// ── Agenda Tipos ──────────────────────────────────────────────────────────────
export const getAgendaTipos   = (user_id) =>
  fetch(`${BASE}/agenda-tipos${user_id ? `?user_id=${user_id}` : ""}`, { headers: headers() }).then(handle);

export const createAgendaTipo = data =>
  fetch(`${BASE}/agenda-tipos`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const updateAgendaTipo = (id, data) =>
  fetch(`${BASE}/agenda-tipos/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(data) }).then(handle);

// opts.reassign_to: nombre del tipo que hereda las entradas
// opts.force: eliminar dejando las entradas sin tipo (grupo "Otros")
export const deleteAgendaTipo = (id, opts={}) => {
  const qs = new URLSearchParams();
  if (opts.reassign_to) qs.set("reassign_to", opts.reassign_to);
  if (opts.force)       qs.set("force", "1");
  const q = qs.toString();
  return fetch(`${BASE}/agenda-tipos/${id}${q ? `?${q}` : ""}`, { method:"DELETE", headers: headers() }).then(handle);
};

// Comparativa de productividad por persona (solo admin). Se resuelve en el
// backend porque GET /agenda devuelve un cuaderno a la vez.
export const getAgendaProductividad = ({desde, hasta}={}) => {
  const q = new URLSearchParams();
  if(desde) q.set("desde", desde);
  if(hasta) q.set("hasta", hasta);
  const qs = q.toString();
  return fetch(`${BASE}/agenda-tipos/productividad${qs ? `?${qs}` : ""}`, { headers: headers() }).then(handle);
};

export const reorderAgendaTipos = (ids, user_id) =>
  fetch(`${BASE}/agenda-tipos/reorder`, { method:"PUT", headers: headers(), body: JSON.stringify({ ids, user_id }) }).then(handle);
// ── Integrations ──────────────────────────────────────────────────────────────
export const getIntegrations   = () =>
  fetch(`${BASE}/integrations`, { headers: headers() }).then(handle);

// Por defecto solo lo conectado; todo:true trae tambien lo desconectado, que es
// lo que necesita la pantalla de gestion de paginas para poder reactivarlo.
export const getNotionItems    = ({ todo = false } = {}) =>
  fetch(`${BASE}/integrations/notion/items${todo ? "?all=1" : ""}`, { headers: headers() }).then(handle);

// Conectar/desconectar una pagina o database en ALM (no toca Notion)
export const setNotionItemEnabled = (externalId, enabled) =>
  fetch(`${BASE}/integrations/notion/items/${externalId}`, { method:"PATCH", headers: headers(), body: JSON.stringify({ enabled }) }).then(handle);

// Que redirect_uri y client_id usara el flujo de OAuth (para diagnosticar)
export const getNotionConfig = () =>
  fetch(`${BASE}/integrations/notion/config`, { headers: headers() }).then(handle);

// La URL de autorizacion se pide con la sesion en la cabecera: el token no viaja en la URL.
// Vuelve a lanzar el consentimiento de Notion para elegir mas paginas. Notion
// muestra ya marcadas las que concediste antes, asi que la seleccion se suma.
export const integrationConnectUrl = (provider) =>
  fetch(`${BASE}/integrations/${provider}/connect-url`, { method:"POST", headers: headers() }).then(handle).then(r => r.url);

export const syncNotion        = () =>
  fetch(`${BASE}/integrations/notion/sync`, { method:"POST", headers: headers() }).then(handle);

export const getNotionPageBlocks = (pageId) =>
  fetch(`${BASE}/integrations/notion/page/${pageId}/blocks`, { headers: headers() }).then(handle);

// Pagina en vivo: valores actuales de todas las propiedades + esquema de su DB
export const getNotionPage = (pageId) =>
  fetch(`${BASE}/integrations/notion/page/${pageId}`, { headers: headers() }).then(handle);

// Edicion de una pagina: { properties?, icon?, archived? }
export const updateNotionPage = (pageId, data) =>
  fetch(`${BASE}/integrations/notion/page/${pageId}`, { method:"PATCH", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const getNotionUsers = () =>
  fetch(`${BASE}/integrations/notion/users`, { headers: headers() }).then(handle);

export const getNotionDatabaseSchema = (dbId) =>
  fetch(`${BASE}/integrations/notion/database/${dbId}/schema`, { headers: headers() }).then(handle);

export const getNotionDatabasePages = (dbId) =>
  fetch(`${BASE}/integrations/notion/database/${dbId}/pages`, { headers: headers() }).then(handle);

// Contenido de la pagina (bloques)
export const addNotionBlock = (pageId, block) =>
  fetch(`${BASE}/integrations/notion/page/${pageId}/blocks`, { method:"POST", headers: headers(), body: JSON.stringify(block) }).then(handle);

export const updateNotionBlock = (blockId, block) =>
  fetch(`${BASE}/integrations/notion/block/${blockId}`, { method:"PATCH", headers: headers(), body: JSON.stringify(block) }).then(handle);

export const deleteNotionBlock = (blockId) =>
  fetch(`${BASE}/integrations/notion/block/${blockId}`, { method:"DELETE", headers: headers() }).then(handle);

// ── Vistas guardadas de Notion (las "pantallas" configurables) ───────────────
export const getNotionViews = (databaseId) =>
  fetch(`${BASE}/notion-vistas${databaseId ? `?database_id=${encodeURIComponent(databaseId)}` : ""}`, { headers: headers() }).then(handle);

export const createNotionView = (view) =>
  fetch(`${BASE}/notion-vistas`, { method:"POST", headers: headers(), body: JSON.stringify(view) }).then(handle);

export const updateNotionView = (id, data) =>
  fetch(`${BASE}/notion-vistas/${id}`, { method:"PATCH", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const deleteNotionView = (id) =>
  fetch(`${BASE}/notion-vistas/${id}`, { method:"DELETE", headers: headers() }).then(handle);

export const getOutlookEvents  = (from, to) => {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to)   params.set("to",   to);
  const qs = params.toString() ? `?${params}` : "";
  return fetch(`${BASE}/integrations/microsoft/events${qs}`, { headers: headers() }).then(handle);
};

// Por que el buscador de reuniones esta vacio: cuenta conectada, ultima
// sincronizacion, cuantos eventos hay cacheados y el ultimo fallo contra Graph.
export const getOutlookEstado  = () =>
  fetch(`${BASE}/integrations/microsoft/estado`, { headers: headers() }).then(handle);

export const syncOutlook       = () =>
  fetch(`${BASE}/integrations/microsoft/sync`, { method:"POST", headers: headers() }).then(handle);

export const disconnectIntegration = (provider) =>
  fetch(`${BASE}/integrations/${provider}`, { method:"DELETE", headers: headers() }).then(handle);

// ── Change Password ───────────────────────────────────────────────────────────
export const changePassword = (currentPassword, newPassword) =>
  fetch(`${BASE}/auth/change-password`, { method:"PUT", headers: headers(), body: JSON.stringify({ currentPassword, newPassword }) }).then(handle);

export const toggleUser = id =>
  fetch(`${BASE}/users/${id}/toggle`, { method:"PUT", headers: headers() }).then(handle);

// ── Notifications ─────────────────────────────────────────────────────────────
export const getNotifications  = () =>
  fetch(`${BASE}/notifications`, { headers: headers() }).then(handle);

export const markNotifRead     = (id) =>
  fetch(`${BASE}/notifications/${id}/read`, { method:"PUT", headers: headers() }).then(handle);

export const markAllNotifsRead = () =>
  fetch(`${BASE}/notifications/read-all`, { method:"PUT", headers: headers() }).then(handle);

export const deleteNotif       = (id) =>
  fetch(`${BASE}/notifications/${id}`, { method:"DELETE", headers: headers() }).then(handle);

export const checkDeadlines    = () =>
  fetch(`${BASE}/notifications/check-deadlines`, { method:"POST", headers: headers() }).then(handle);

// ── Calendarios ───────────────────────────────────────────────────────────────
export const getCalendarios    = () =>
  fetch(`${BASE}/calendarios`, { headers: headers() }).then(handle);

export const createCalendario  = data =>
  fetch(`${BASE}/calendarios`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const updateCalendario  = (id, data) =>
  fetch(`${BASE}/calendarios/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const deleteCalendario  = (id, force=false) =>
  fetch(`${BASE}/calendarios/${id}${force ? "?force=1" : ""}`, { method:"DELETE", headers: headers() }).then(handle);

export const getEventosCal     = (from, to) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to)   qs.set("to",   to);
  const q = qs.toString();
  return fetch(`${BASE}/calendarios/eventos${q ? `?${q}` : ""}`, { headers: headers() }).then(handle);
};

export const createEventoCal   = data =>
  fetch(`${BASE}/calendarios/eventos`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const updateEventoCal   = (id, data) =>
  fetch(`${BASE}/calendarios/eventos/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const deleteEventoCal   = id =>
  fetch(`${BASE}/calendarios/eventos/${id}`, { method:"DELETE", headers: headers() }).then(handle);

export const importarCalendario = (id, text, anual=false) =>
  fetch(`${BASE}/calendarios/${id}/importar`, { method:"POST", headers: headers(), body: JSON.stringify({ text, anual }) }).then(handle);

export const duplicarAnioCalendario = (id, from_year, to_year) =>
  fetch(`${BASE}/calendarios/${id}/duplicar-anio`, { method:"POST", headers: headers(), body: JSON.stringify({ from_year, to_year }) }).then(handle);

// ── Vacaciones ────────────────────────────────────────────────────────────────
export const getVacaciones    = (from, to) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to)   qs.set("to",   to);
  const q = qs.toString();
  return fetch(`${BASE}/vacaciones${q ? `?${q}` : ""}`, { headers: headers() }).then(handle);
};

export const createVacacion   = data      =>
  fetch(`${BASE}/vacaciones`, { method:"POST", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const updateVacacion   = (id,data) =>
  fetch(`${BASE}/vacaciones/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(data) }).then(handle);

export const deleteVacacion   = id        =>
  fetch(`${BASE}/vacaciones/${id}`, { method:"DELETE", headers: headers() }).then(handle);

// Los tipos de ausencia vivos de Odoo, con el cupo que le queda a quien
// pregunta. Son 16 y cambian de id cada año, asi que no se cachean.
// Con user_id, los de OTRA persona: el cupo se lee con la sesion de cada uno,
// asi que preguntando con la propia salen los cupos propios, no los suyos.
export const getTiposOdoo     = (userId) =>
  fetch(`${BASE}/vacaciones/tipos-odoo${userId ? `?user_id=${userId}` : ""}`,
        { headers: headers() }).then(handle);

// Empuja un borrador a Odoo: pasa a 'solicitada' y le llega a RRHH.
export const solicitarVacacion = (id, odoo_tipo_id) =>
  fetch(`${BASE}/vacaciones/${id}/solicitar`, { method:"POST", headers: headers(),
         body: JSON.stringify({ odoo_tipo_id }) }).then(handle);

// ── Guardias ──────────────────────────────────────────────────────────────────
export const getGuardias      = (from, to) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to)   qs.set("to",   to);
  const q = qs.toString();
  return fetch(`${BASE}/guardias${q ? `?${q}` : ""}`, { headers: headers() }).then(handle);
};

// semana = lunes en formato AAAA-MM-DD; user_id null desasigna
export const setGuardia       = (semana, user_id, nota=null) =>
  fetch(`${BASE}/guardias/${semana}`, { method:"PUT", headers: headers(), body: JSON.stringify({ user_id, nota }) }).then(handle);

// El cuadrante lo planifica el cliente (planificarGuardias en utils.js); aqui
// solo se guarda la lista ya resuelta de {semana, user_id}.
export const asignarLoteGuardias = (asignaciones) =>
  fetch(`${BASE}/guardias/asignar-lote`, { method:"POST", headers: headers(),
    body: JSON.stringify({ asignaciones }) }).then(handle);

export const limpiarGuardias  = (desde, hasta) =>
  fetch(`${BASE}/guardias/limpiar`, { method:"POST", headers: headers(), body: JSON.stringify({ desde, hasta }) }).then(handle);

// ── Importación de cuadrantes ─────────────────────────────────────────────────
// Con comprobar:true no escribe nada: solo informa de qué haría y de qué nombres
// no ha sabido resolver. La segunda llamada llega con el `mapa` ya completo.
export const importarGuardias   = (text, mapa={}, comprobar=false) =>
  fetch(`${BASE}/guardias/importar`, { method:"POST", headers: headers(), body: JSON.stringify({ text, mapa, comprobar }) }).then(handle);

// Pone el calendario al día con lo que Odoo dice AHORA, con la sesión de quien
// mira. Un 409 SIN_CONEXION no es un error que haya que gritar: significa que
// esa persona no tiene Odoo conectado, y el calendario funciona igual con lo
// que ya hay guardado.
export const sincronizarVacacionesOdoo = (from, to) =>
  fetch(`${BASE}/vacaciones/sincronizar-odoo`, { method:"POST", headers: headers(),
    body: JSON.stringify({ from, to }) }).then(handle);

export const importarVacaciones = (text, mapa={}, comprobar=false) =>
  fetch(`${BASE}/vacaciones/importar`, { method:"POST", headers: headers(), body: JSON.stringify({ text, mapa, comprobar }) }).then(handle);

// ── Fichaje (Odoo) ────────────────────────────────────────────────────────────
// La contraseña de Odoo solo viaja en conectarOdoo(); el backend guarda la
// cookie de sesión, nunca la contraseña.
export const conectarOdoo    = (login, password) =>
  fetch(`${BASE}/fichaje/conectar`, { method:"POST", headers: headers(), body: JSON.stringify({ login, password }) }).then(handle);

export const desconectarOdoo = () =>
  fetch(`${BASE}/fichaje/conectar`, { method:"DELETE", headers: headers() }).then(handle);

export const getFichajeEstado = () =>
  fetch(`${BASE}/fichaje/estado`, { headers: headers() }).then(handle);

// Quien esta dentro ahora mismo segun el fichaje de Odoo, en el ambito del
// registro de jornada. Devuelve tambien a los que estan fuera: "ha salido" y "de
// esa persona no sabemos nada" no son lo mismo y el tablero los pinta distinto.
export const getPresenciaEquipo = () =>
  fetch(`${BASE}/fichaje/presencia`, { headers: headers() }).then(handle);

// `accion` es la que el widget cree que procede; el backend la rechaza si el
// estado real en Odoo ha cambiado (doble click, otra pestaña, autocierre).
export const fichar          = (accion, geo = null) =>
  fetch(`${BASE}/fichaje`, { method:"POST", headers: headers(),
    body: JSON.stringify(geo ? { accion, geo } : { accion }) }).then(handle);


// ── Helpdesk (Odoo · helpdesk_mgmt de OCA) ────────────────────────────────────
export const getHelpdeskMeta    = () =>
  fetch(`${BASE}/helpdesk/meta`, { headers: headers() }).then(handle);

// Para ligar una incidencia ya cerrada al Cuaderno: getHelpdeskTickets({ cerradas: 1, mine: 1, q, dias }) devuelve solo las cerradas,
// la de cierre mas reciente primero (tope 50, ultimos 90 dias por defecto), con `cerrada` y `cerrado_el` en cada una.
export const getHelpdeskTickets = (filtros = {}) => {
  const q = new URLSearchParams();
  Object.entries(filtros).forEach(([k, v]) => { if (v !== "" && v != null) q.set(k, v); });
  return fetch(`${BASE}/helpdesk/tickets?${q}`, { headers: headers() }).then(handle);
};

export const getHelpdeskTicket  = id =>
  fetch(`${BASE}/helpdesk/tickets/${id}`, { headers: headers() }).then(handle);


export const getHelpdeskKanban  = (filtros = {}) => {
  const q = new URLSearchParams();
  Object.entries(filtros).forEach(([k, v]) => { if (v !== "" && v != null) q.set(k, v); });
  return fetch(`${BASE}/helpdesk/kanban?${q}`, { headers: headers() }).then(handle);
};

export const getHelpdeskAdjuntos = id =>
  fetch(`${BASE}/helpdesk/tickets/${id}/adjuntos`, { headers: headers() }).then(handle);

// El binario pasa por el backend porque el navegador no tiene sesión de Odoo.
// Se descarga con el token en la cabecera y se convierte en object URL: un <img
// src> no puede enviar cabeceras, y meter el token en la URL lo dejaría escrito
// en logs e historial.
export const getHelpdeskAdjuntoUrl = async (id, attId) => {
  const res = await fetch(`${BASE}/helpdesk/tickets/${id}/adjuntos/${attId}`, { headers: headers() });
  // La respuesta buena es binaria, asi que esto no puede pasar por handle(); el
  // error, en cambio, si llega en JSON y con `code` (SIN_CONEXION, SESION...) y
  // hay pantallas que lo distinguen. Sin esto, una sesion de Odoo caducada se
  // veia igual que un adjunto roto.
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || "No se pudo descargar el adjunto");
    if (data.code) err.code = data.code;
    throw err;
  }
  return URL.createObjectURL(await res.blob());
};

// Sin desde_id devuelve solo el cursor: si no, la primera consulta avisaría de
// los 3.383 tickets acumulados.
export const getHelpdeskNuevos = desdeId =>
  fetch(`${BASE}/helpdesk/nuevos${desdeId ? `?desde_id=${desdeId}` : ""}`, { headers: headers() }).then(handle);

export const getHelpdeskUsuarios = () =>
  fetch(`${BASE}/helpdesk/usuarios`, { headers: headers() }).then(handle);

// Asignar responsable o mover de etapa. Odoo puede denegarlo por regla de
// registro aunque el permiso de modelo lo permita: el mensaje se muestra tal cual.
export const actualizarTicket = (id, cambios) =>
  fetch(`${BASE}/helpdesk/tickets/${id}`, { method:"PATCH", headers: headers(), body: JSON.stringify(cambios) }).then(handle);

// El hilo del ticket (chatter de Odoo). La descripción es solo el primer correo.
export const getHelpdeskMensajes = id =>
  fetch(`${BASE}/helpdesk/tickets/${id}/mensajes`, { headers: headers() }).then(handle);

// `interno: false` publica en el hilo y notifica al cliente; `true` es nota
// interna. Un mensaje enviado no se puede retirar.
export const responderTicket = (id, cuerpo, interno = false) =>
  fetch(`${BASE}/helpdesk/tickets/${id}/mensajes`, {
    method:"POST", headers: headers(), body: JSON.stringify({ cuerpo, interno }),
  }).then(handle);

// Avatares de Odoo, cacheados en memoria como data URI.
//
// Se piden EN LOTE: uno por persona agotaba el limitador de peticiones de la
// API (300 cada 15 min por IP) en cuanto se abria un desplegable con 40
// usuarios, y el bloqueo alcanzaba tambien al login.
const _avatares = new Map();   // "modelo:id" -> dataUri | null

export const precargarAvatares = async (modelo, ids) => {
  const faltan = [...new Set(ids)].filter(id => id && !_avatares.has(`${modelo}:${id}`));
  if (!faltan.length) return;
  // Se marcan antes de pedirlos para que dos componentes montandose a la vez no
  // lancen la misma peticion dos veces.
  faltan.forEach(id => _avatares.set(`${modelo}:${id}`, null));
  try {
    const r = await fetch(`${BASE}/helpdesk/avatares?modelo=${modelo}&ids=${faltan.join(",")}`,
                          { headers: headers() });
    if (!r.ok) return;
    const datos = await r.json();
    Object.entries(datos).forEach(([id, uri]) => _avatares.set(`${modelo}:${id}`, uri));
  } catch { /* sin avatares se muestran las iniciales */ }
};

export const avatarCacheado = (modelo, id) => _avatares.get(`${modelo}:${id}`) || null;

// Trae a la BD de ALM los fichajes que existan en Odoo y aqui no.
export const sincronizarFichajes = (dias) =>
  fetch(`${BASE}/fichaje/sincronizar`, { method:"POST", headers: headers(),
         body: JSON.stringify(dias ? { dias } : {}) }).then(handle);

// Mis tramos de jornada segun Odoo, para el cuadro de mandos semanal.
export const getMisTramos = (from, to) =>
  fetch(`${BASE}/fichaje/mis-tramos?from=${from}&to=${to}`, { headers: headers() }).then(handle);

// Jornada de trabajo por persona (calendario de Odoo o 8 h L-V por defecto). Sin ids, la mia.
export const getJornada = ids =>
  fetch(`${BASE}/jornada${ids?.length ? `?user_ids=${ids.join(",")}` : ""}`, { headers: headers() }).then(handle);

// Descarta del registro un fichaje que no llego a Odoo. Solo administradores y
// solo los fallidos: el backend rechaza los que si estan en Odoo.
export const borrarFichaje = id =>
  fetch(`${BASE}/fichaje/asistencias/${id}`, { method:"DELETE", headers: headers() }).then(handle);

// Registro de asistencias de toda la plantilla. Solo administradores: el
// backend responde 403 a cualquier otro rol.
export const getAsistencias = (from, to) => {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to)   q.set("to", to);
  return fetch(`${BASE}/fichaje/asistencias?${q}`, { headers: headers() }).then(handle);
};

// ── Proyectos (gestor nativo) ─────────────────────────────────────────────────
// El gestor propio, distinto de la integracion de Notion de arriba: aquel lee y
// escribe en Notion, este es de ALM. Notion sigue siendo una pestaña dentro de
// la seccion Proyectos.
//
// El permiso de escritura NO se decide en el cliente: cada fila llega con
// `can_edit` resuelto por backend/lib/permisos.js. Ver el espejo cosmetico en
// src/utils.js.

// Monta la query string omitiendo lo vacio: los filtros de la vista general se
// pasan tal cual desde un solo objeto de estado.
const queryStr = o => {
  const p = new URLSearchParams();
  Object.entries(o || {}).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) p.set(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const getProyectos   = f  => fetch(`${BASE}/proyectos${queryStr(f)}`, { headers: headers() }).then(handle);

// «Mis tareas»: la lista agrupada y una sola llamada para actuar sobre una tarea. Si la fila
// ya existe en la bandeja personal (trae `id`) se actualiza por id; si es un paso asignado
// que aun no tiene fila, se crea/actualiza por su task_id.
export const getMisTareas = () => fetch(`${BASE}/mis-tareas`, { headers: headers() }).then(handle);
// Notion al abrir «Tareas»: el servidor mira si lleva mas de 4 h sin sincronizar y, si toca, lo hace en segundo plano
export const notionAlDia = () => fetch(`${BASE}/mis-tareas/notion/al-dia`, { method: "POST", headers: headers() }).then(handle);
// Las ultimas hechas de la persona (tope 50) para «Ver hechas» y «Recuperar».
export const getMisTareasHechas = () => fetch(`${BASE}/mis-tareas/hechas`, { headers: headers() }).then(handle);
export const actuarMisTarea = (item, cambios) => {
  const url = item.id != null
    ? `${BASE}/mis-tareas/${encodeURIComponent(item.id)}`
    : `${BASE}/mis-tareas/proyecto/${encodeURIComponent(item.task_id)}`;
  return fetch(url, { method: item.id != null ? "PATCH" : "PUT", headers: headers(), body: JSON.stringify(cambios) }).then(handle);
};
// Encargos: mandar una tarea suelta a alguien de mi equipo (T-109). `receptores` vacio = no puedo mandar.
// q (nombre o equipo) se aplica en el servidor; sin q, hasta 200 personas y `total` dice cuantas hay en realidad.
export const getReceptoresEncargo = q =>
  fetch(`${BASE}/encargos/receptores${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: headers() }).then(handle);
export const mandarEncargo = cuerpo =>
  fetch(`${BASE}/encargos`, { method: "POST", headers: headers(), body: JSON.stringify(cuerpo) }).then(handle);
// Proponer una tarea (quien no es gestor ni admin): la aprueba un gestor o admin del equipo de quien la recibe. Sin receptor_id es para uno mismo.
export const proponerEncargo = cuerpo =>
  fetch(`${BASE}/encargos/proponer`, { method: "POST", headers: headers(), body: JSON.stringify(cuerpo) }).then(handle);
// Las propuestas que esta persona (gestor o admin) puede aprobar o rechazar; un member recibe lista vacia.
export const getPorAprobar = () => fetch(`${BASE}/encargos/por-aprobar`, { headers: headers() }).then(handle);
export const aprobarEncargo = id =>
  fetch(`${BASE}/encargos/${encodeURIComponent(id)}/aprobar`, { method: "PUT", headers: headers() }).then(handle);
export const rechazarEncargo = (id, motivo) =>
  fetch(`${BASE}/encargos/${encodeURIComponent(id)}/rechazar`, { method: "PUT", headers: headers(), body: JSON.stringify(motivo ? { motivo } : {}) }).then(handle);
export const getEncargosEnviados = () => fetch(`${BASE}/encargos/enviados`, { headers: headers() }).then(handle);
export const noPuedoEncargo = (id, motivo) =>
  fetch(`${BASE}/encargos/${encodeURIComponent(id)}/no-puedo`, { method: "PUT", headers: headers(), body: JSON.stringify({ motivo }) }).then(handle);
export const getMensajesEncargo = id => fetch(`${BASE}/encargos/${encodeURIComponent(id)}/mensajes`, { headers: headers() }).then(handle);
export const enviarMensajeEncargo = (id, texto) =>
  fetch(`${BASE}/encargos/${encodeURIComponent(id)}/mensajes`, { method: "POST", headers: headers(), body: JSON.stringify({ texto }) }).then(handle);
// q solo lo atiende el servidor para quien puede buscar en toda la plantilla (`puede_buscar`).
export const getCargaEquipo = q =>
  fetch(`${BASE}/encargos/carga${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: headers() }).then(handle);
// Explorador de Notion (T-138): navegar por lo que la persona compartio con ALM. Sin parametros, los espacios; con `espacio` o `id`, lo que hay dentro
// (sin `espacio` vale el unico); con `q`, busqueda plana por titulo; `cursor` pide la pagina siguiente. Errores con `code`: SIN_CONEXION, NO_ENCONTRADO, BUSQUEDA_CORTA.
export const exploradorNotion = ({ espacio, id, q, limit, cursor } = {}) => {
  const p = new URLSearchParams();
  Object.entries({ espacio, id, q, limit, cursor }).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, v); });
  return fetch(`${BASE}/integrations/notion/explorar${p.toString() ? `?${p}` : ""}`, { headers: headers() }).then(handle);
};
// Planificar una tarea (hoy, un dia o esta semana) y su recordatorio en Microsoft To Do.
// Los errores llevan `code` (DIA_LLENO, SIN_DIA, MIGRACION...).
export const planificarMisTarea = (item, cuerpo) => {
  const url = item.id != null
    ? `${BASE}/mis-tareas/${encodeURIComponent(item.id)}/plan`
    : `${BASE}/mis-tareas/proyecto/${encodeURIComponent(item.task_id)}/plan`;
  return fetch(url, { method: "PUT", headers: headers(), body: JSON.stringify(cuerpo) }).then(handle);
};
// Traer a Mis tareas una tarea de fuera al empezarla (T-107): { origen: "paso"|"notion", task_id | external_id }.
// Devuelve { ok, id, creada }. «Deshacer» revierte solo un alta hecha asi.
export const traerAMisTareas = cuerpo =>
  fetch(`${BASE}/mis-tareas/traer`, { method: "POST", headers: headers(), body: JSON.stringify(cuerpo) }).then(handle);
export const deshacerTraidaMisTarea = id =>
  fetch(`${BASE}/mis-tareas/${encodeURIComponent(id)}/traida`, { method: "DELETE", headers: headers() }).then(handle);
export const ponerRecordatorioMisTarea = (id, cuerpo) =>
  fetch(`${BASE}/mis-tareas/${encodeURIComponent(id)}/recordatorio`, { method: "PUT", headers: headers(), body: JSON.stringify(cuerpo) }).then(handle);
export const quitarRecordatorioMisTarea = id =>
  fetch(`${BASE}/mis-tareas/${encodeURIComponent(id)}/recordatorio`, { method: "DELETE", headers: headers() }).then(handle);
// Notion opt-in: traer paginas a «Mis tareas» (por ids, o por filtro con todos:true que evalua
// el servidor), saber cuales ya estan y quitar una. Devuelve { añadidas, ya_estaban, total, omitidas, truncado }.
export const getIdsNotionMisTareas = () => fetch(`${BASE}/mis-tareas/ids-notion`, { headers: headers() }).then(handle);
export const anadirDesdeNotion = cuerpo =>
  fetch(`${BASE}/mis-tareas/desde-notion`, { method: "POST", headers: headers(), body: JSON.stringify(cuerpo) }).then(handle);
export const quitarNotionDeMisTareas = externalId =>
  fetch(`${BASE}/mis-tareas/notion/${encodeURIComponent(externalId)}`, { method: "DELETE", headers: headers() }).then(handle);
export const getProyecto    = id => fetch(`${BASE}/proyectos/${id}`, { headers: headers() }).then(handle);

// ── Protocolos de actuacion ─────────────────────────────────────────────────
// Un protocolo es un proyecto marcado, asi que comparte ruta y formato con
// ellos: getPlantillas devuelve las mismas tarjetas que getProyectos, y para
// abrir o editar uno valen getProyecto y todo lo de los pasos.
export const getPlantillas = () =>
  fetch(`${BASE}/proyectos?plantillas=1`, { headers: headers() }).then(handle);

export const guardarComoPlantilla = (id, d) =>
  fetch(`${BASE}/proyectos/${id}/plantilla`,
    { method:"POST", headers: headers(), body: JSON.stringify(d || {}) }).then(handle);

export const instanciarPlantilla = (id, d) =>
  fetch(`${BASE}/proyectos/${id}/instanciar`,
    { method:"POST", headers: headers(), body: JSON.stringify(d || {}) }).then(handle);

export const createProyecto = d =>
  fetch(`${BASE}/proyectos`, { method:"POST", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const updateProyecto = (id, d) =>
  fetch(`${BASE}/proyectos/${id}`, { method:"PUT", headers: headers(), body: JSON.stringify(d) }).then(handle);

// Sin `duro` archiva; con duro=1 borra de verdad y solo lo permite sistemas.
export const deleteProyecto = (id, duro) =>
  fetch(`${BASE}/proyectos/${id}${duro ? "?duro=1" : ""}`, { method:"DELETE", headers: headers() }).then(handle);

export const addProyectoMiembro = (id, d) =>
  fetch(`${BASE}/proyectos/${id}/miembros`, { method:"POST", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const updateProyectoMiembro = (id, uid, d) =>
  fetch(`${BASE}/proyectos/${id}/miembros/${uid}`, { method:"PUT", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const removeProyectoMiembro = (id, uid) =>
  fetch(`${BASE}/proyectos/${id}/miembros/${uid}`, { method:"DELETE", headers: headers() }).then(handle);

export const getTareas = (id, f) =>
  fetch(`${BASE}/proyectos/${id}/tareas${queryStr(f)}`, { headers: headers() }).then(handle);

export const createTarea = (id, d) =>
  fetch(`${BASE}/proyectos/${id}/tareas`, { method:"POST", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const updateTarea = (id, tid, d) =>
  fetch(`${BASE}/proyectos/${id}/tareas/${tid}`, { method:"PUT", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const deleteTarea = (id, tid) =>
  fetch(`${BASE}/proyectos/${id}/tareas/${tid}`, { method:"DELETE", headers: headers() }).then(handle);

export const reordenarTareas = (id, orden) =>
  fetch(`${BASE}/proyectos/${id}/tareas/orden`, { method:"PUT", headers: headers(), body: JSON.stringify({ orden }) }).then(handle);

export const setTareaAsignaciones = (id, tid, d) =>
  fetch(`${BASE}/proyectos/${id}/tareas/${tid}/asignaciones`, { method:"PUT", headers: headers(), body: JSON.stringify(d) }).then(handle);

// Posiciones del lienzo en lote: un solo PUT con todo lo que se haya movido.
export const guardarPosiciones = (id, nodos) =>
  fetch(`${BASE}/proyectos/${id}/nodos/posiciones`, { method:"PUT", headers: headers(), body: JSON.stringify({ nodos }) }).then(handle);

export const getDependencias = id =>
  fetch(`${BASE}/proyectos/${id}/dependencias`, { headers: headers() }).then(handle);

export const createDependencia = (id, d) =>
  fetch(`${BASE}/proyectos/${id}/dependencias`, { method:"POST", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const updateDependencia = (id, did, d) =>
  fetch(`${BASE}/proyectos/${id}/dependencias/${did}`, { method:"PUT", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const deleteDependencia = (id, did) =>
  fetch(`${BASE}/proyectos/${id}/dependencias/${did}`, { method:"DELETE", headers: headers() }).then(handle);

// Los pasos que le tocan a quien pregunta, de todos los proyectos que ve. Lo
// consume el cuaderno para poder enlazar una entrada con un paso.
//
// `desbloqueo` anade a cada paso si se puede EMPEZAR ya (`bloqueado`) y a que
// espera si no (`bloqueada_por`). Lo pide el cronometro y NO el cuaderno: alli
// se imputan horas a posteriori, tambien a pasos ya cerrados, y ese calculo
// obliga al servidor a leer el grafo completo de cada proyecto.
export const getMisPasos = (desbloqueo = false) =>
  fetch(`${BASE}/proyectos/tareas/asignadas${desbloqueo ? "?desbloqueo=1" : ""}`,
        { headers: headers() }).then(handle);

export const getDesbloqueoPaso = (projectId, taskId) =>
  fetch(`${BASE}/proyectos/${projectId}/tareas/${taskId}/desbloqueo`,
        { headers: headers() }).then(handle);

export const getProyectoHistorial = (id, f) =>
  fetch(`${BASE}/proyectos/${id}/historial${queryStr(f)}`, { headers: headers() }).then(handle);

export const getHistorialReciente = limit =>
  fetch(`${BASE}/proyectos/historial/reciente${queryStr({ limit })}`, { headers: headers() }).then(handle);

// Tiempo real del cuaderno agregado en SQL. agrupar = tarea|persona|dia|tipo.
// La respuesta trae `parcial: true` cuando el servidor ha limitado el desglose
// por persona a quien pregunta (no es admin ni gestor): la UI lo avisa.
export const getProyectoTiempo = (id, f) =>
  fetch(`${BASE}/proyectos/${id}/tiempo${queryStr(f)}`, { headers: headers() }).then(handle);

// ── Cronómetro de tarea activa ────────────────────────────────────────────────
// El cronómetro en curso y los que quedan en pausa viven en el SERVIDOR, no en
// el navegador: recargar tiene que dar el mismo número, y el tiempo del día
// acaba en el cuaderno. Cada respuesta trae el estado completo más `ahora`, la
// hora del servidor, para que el cliente corrija el desfase de su propio reloj
// en vez de fiarse de él.
export const getCronometro = () =>
  fetch(`${BASE}/cronometro`, { headers: headers() }).then(handle);

// { type, description, sin_enlazar?, task_id?, external_task_id?,
//   external_project_id?, incidencia_id?, incidencia_ref?, incidencia_titulo? }
//
// `origen` es opcional: sin él, el servidor usa la asignación predeterminada del tipo
// (agenda_tipos.origen); con él, cualquiera de las cuatro con su enlace. Y
// empezar pausa lo que estuviera corriendo, en la misma transacción, para que no
// queden dos activos ni se pierda el tramo del anterior.
export const iniciarCronometro = d =>
  fetch(`${BASE}/cronometro/iniciar`, { method:"POST", headers: headers(), body: JSON.stringify(d) }).then(handle);

export const pausarCronometro = id =>
  fetch(`${BASE}/cronometro/${id}/pausar`, { method:"POST", headers: headers() }).then(handle);

// Ojo: reanudar un borrador de AYER devuelve un id nuevo (`reanudado`). El día
// se congela al crear el cronómetro, así que las horas de hoy no pueden caer en
// la fila de ayer.
export const reanudarCronometro = id =>
  fetch(`${BASE}/cronometro/${id}/reanudar`, { method:"POST", headers: headers() }).then(handle);

// Cierra y vuelca al cuaderno. `finalizar_tarea` cierra además el paso del
// proyecto (status → Completada) y con ello desbloquea lo que dependa de él.
export const pararCronometro = (id, opts={}) =>
  fetch(`${BASE}/cronometro/${id}/parar`, { method:"POST", headers: headers(), body: JSON.stringify(opts) }).then(handle);

// Descarta el borrador. Si ya había escrito en el cuaderno, la entrada se
// conserva: borrarla desde aquí destruiría horas que pueden estar corregidas a
// mano. La respuesta lo dice en `entrada_conservada`.
export const borrarCronometro = id =>
  fetch(`${BASE}/cronometro/${id}`, { method:"DELETE", headers: headers() }).then(handle);

// Congela todos los tramos abiertos. Se llama JUSTO ANTES de fichar salida: si
// se hiciera después, el tramo seguiría contando con la persona ya fuera.
export const pausarCronometros = () =>
  fetch(`${BASE}/cronometro/pausar-todo`, { method:"POST", headers: headers() }).then(handle);

// En que anda ahora la gente que uno puede ver. El ambito es el mismo que el
// del registro de asistencias: quien puede ver las horas de alguien puede ver en
// que las esta gastando. Solo devuelve a quien tiene algo hoy.
export const getActividadEquipo = () =>
  fetch(`${BASE}/cronometro/equipo`, { headers: headers() }).then(handle);

export const getResumenJornada = () =>
  fetch(`${BASE}/cronometro/jornada/resumen`, { headers: headers() }).then(handle);

// El cierre del día en una sola operación: N entradas de cuaderno y hasta N
// pasos cerrados. Va en un POST y no en N llamadas porque a medio camino el día
// quedaría contado a trozos. Es idempotente: reintentar tras un corte es seguro.
export const cerrarJornadaCronometros = (completar_tareas=[]) =>
  fetch(`${BASE}/cronometro/jornada/cerrar`, { method:"POST", headers: headers(),
    body: JSON.stringify({ completar_tareas }) }).then(handle);

// «Mi lista de tareas» de Notion (T-140): elegir una base como fuente. previa no guarda nada.
const jsonPost = (url, method, body) => fetch(`${BASE}${url}`, { method, headers: headers(), body: JSON.stringify(body || {}) }).then(handle);
export const fuentesNotion        = () => fetch(`${BASE}/integrations/notion/fuentes`, { headers: headers() }).then(handle);
export const previaFuenteNotion   = body => jsonPost("/integrations/notion/fuentes/previa", "POST", body);
export const guardarFuenteNotion  = body => jsonPost("/integrations/notion/fuentes", "POST", body);
export const cambiarFuenteNotion  = (id, body) => jsonPost(`/integrations/notion/fuentes/${id}`, "PATCH", body);
export const quitarFuenteNotion   = (id, { retirar = false } = {}) =>
  fetch(`${BASE}/integrations/notion/fuentes/${id}${retirar ? "?retirar=1" : ""}`, { method: "DELETE", headers: headers() }).then(handle);
// E3 (T-142): contar antes de quitar una lista, lo nuevo por lista, quedarse con una tarea reasignada y deshacer el modo automatico
export const quitarPreviaFuenteNotion = id => fetch(`${BASE}/integrations/notion/fuentes/${id}/quitar-previa`, { headers: headers() }).then(handle);
export const fuentesNuevasNotion = () => fetch(`${BASE}/mis-tareas/fuentes-nuevas`, { headers: headers() }).then(handle);
export const mantenerMisTarea = id => fetch(`${BASE}/mis-tareas/${encodeURIComponent(id)}/mantener`, { method: "PUT", headers: headers() }).then(handle);
export const deshacerFuentesNotion = cuerpo => jsonPost("/mis-tareas/fuentes-deshacer", "POST", cuerpo);
